import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth, ROLES_FULL_ACCESS, ROLES_MANAGEMENT } from "../../App";
import { format as dateFnsFormat } from "date-fns";
import { fr as dateFnsFr } from "date-fns/locale";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { toast } from "sonner";
import {
  Loader2,
  Save,
  Plus,
  Trash2,
  User,
  Euro,
  FileText,
  Calendar,
  Users,
  Lock,
  X,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  CalendarDays,
  Pencil,
  ExternalLink,
  ChevronRight,
  ClipboardList,
  SkipForward,
} from "lucide-react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

// ── Helpers ──────────────────────────────────────────────────────────────────

const ROLE_CONFIG = {
  admin: { label: "Admin", className: "bg-purple-600 text-white" },
  hr: { label: "RH", className: "bg-[#E07A5F] text-white" },
  manager: { label: "Manager", className: "bg-[#1A4D2E] text-white" },
  collaborateur: { label: "Collaborateur", className: "bg-[#E8F5E9] text-[#1A4D2E]" },
  employee: { label: "Collaborateur", className: "bg-[#E8F5E9] text-[#1A4D2E]" },
  comptabilité: { label: "Comptabilité", className: "bg-blue-600 text-white" },
};

const NOTE_TYPES = [
  { value: "entretien_annuel", label: "Entretien annuel" },
  { value: "entretien_pro", label: "Entretien professionnel" },
  { value: "avertissement", label: "Avertissement" },
  { value: "felicitation", label: "Félicitation" },
  { value: "note_rh", label: "Note RH" },
  { value: "autre", label: "Autre" },
];

const NOTE_COLORS = {
  entretien_annuel: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  entretien_pro: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400",
  avertissement: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  felicitation: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  note_rh: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  autre: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
};

const STATUS_LEAVE = {
  pending: { label: "En attente", icon: Clock, badgeClass: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  approved: { label: "Approuvé", icon: CheckCircle, badgeClass: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  rejected: { label: "Refusé", icon: XCircle, badgeClass: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
};

const getInitials = (fn, ln) => `${fn?.[0] || ""}${ln?.[0] || ""}`.toUpperCase();

const DEPT_OPTIONS = [
  "Expert métier",
  "Ressources Humaines",
  "Direction",
  "Technique",
];

const fmt = (val, suffix = "€") =>
  val != null ? `${Number(val).toLocaleString("fr-FR")} ${suffix}` : "—";

// ── Tab: Informations ─────────────────────────────────────────────────────────

const TabInfos = ({ userData, allUsers, canEdit, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      first_name: userData.first_name || "",
      last_name: userData.last_name || "",
      email: userData.email || "",
      phone: userData.phone || "",
      department: userData.department || "",
      position: userData.position || "",
      hire_date: userData.hire_date || "",
      birth_date: userData.birth_date || "",
      address: userData.address || "",
      manager_id: userData.manager_id || "",
    });
  }, [userData]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/${userData.id}`, form);
      toast.success("Informations mises à jour");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const managers = allUsers.filter(
    (u) => ["manager", "hr", "admin"].includes(u.role) && u.id !== userData.id
  );

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label className="dark:text-gray-300">Prénom</Label>
          <Input value={form.first_name || ""} onChange={(e) => set("first_name", e.target.value)}
            disabled={!canEdit} className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
        </div>
        <div className="space-y-1">
          <Label className="dark:text-gray-300">Nom</Label>
          <Input value={form.last_name || ""} onChange={(e) => set("last_name", e.target.value)}
            disabled={!canEdit} className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label className="dark:text-gray-300">Email</Label>
          <Input value={form.email || ""} disabled
            className="dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400 cursor-not-allowed" />
        </div>
        <div className="space-y-1">
          <Label className="dark:text-gray-300">Téléphone</Label>
          <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)}
            placeholder="+33 6 00 00 00 00" disabled={!canEdit}
            className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label className="dark:text-gray-300">Département</Label>
          {canEdit ? (
            <Select value={form.department || "__none__"} onValueChange={(v) => set("department", v === "__none__" ? "" : v)}>
              <SelectTrigger className="dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                <SelectValue placeholder="Sélectionner un département" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Non défini</SelectItem>
                {DEPT_OPTIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input value={form.department || ""} disabled className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          )}
        </div>
        <div className="space-y-1">
          <Label className="dark:text-gray-300">Poste</Label>
          <Input value={form.position || ""} onChange={(e) => set("position", e.target.value)}
            disabled={!canEdit} className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label className="dark:text-gray-300">Date d'embauche</Label>
          <Input type="date" value={form.hire_date || ""} onChange={(e) => set("hire_date", e.target.value)}
            disabled={!canEdit} className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
        </div>
        <div className="space-y-1">
          <Label className="dark:text-gray-300">Date de naissance</Label>
          <Input type="date" value={form.birth_date || ""} onChange={(e) => set("birth_date", e.target.value)}
            disabled={!canEdit} className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
        </div>
      </div>

      {canEdit && (
        <div className="space-y-1">
          <Label className="dark:text-gray-300">Manager</Label>
          <Select value={form.manager_id || "__none__"}
            onValueChange={(v) => set("manager_id", v === "__none__" ? "" : v)}>
            <SelectTrigger className="dark:bg-gray-700 dark:border-gray-600 dark:text-white">
              <SelectValue placeholder="Aucun manager" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Aucun manager</SelectItem>
              {managers.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.first_name} {m.last_name} ({ROLE_CONFIG[m.role]?.label || m.role})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-1">
        <Label className="dark:text-gray-300">Adresse</Label>
        <Textarea value={form.address || ""} onChange={(e) => set("address", e.target.value)}
          placeholder="Adresse complète" rows={2} disabled={!canEdit}
          className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
      </div>

      {canEdit && (
        <Button onClick={handleSave} disabled={saving}
          className="w-full bg-[#1A4D2E] hover:bg-[#143D24] text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      )}
    </div>
  );
};

// ── Tab: Rémunération ─────────────────────────────────────────────────────────

const TabRemuneration = ({ userId }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({
    effective_date: "", gross_salary: "", bonus: "",
    salary_type: "mensuel", note: "",
  });

  const fetchEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users/${userId}/salary-history`);
      setEntries(res.data);
    } catch (e) {
      toast.error("Erreur chargement rémunération");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const openAdd = () => {
    setEditingEntry(null);
    setForm({ effective_date: "", gross_salary: "", bonus: "", salary_type: "mensuel", note: "" });
    setShowForm(true);
  };

  const openEdit = (e) => {
    setEditingEntry(e);
    setForm({
      effective_date: e.effective_date,
      gross_salary: String(e.gross_salary),
      bonus: e.bonus != null ? String(e.bonus) : "",
      salary_type: e.salary_type,
      note: e.note || "",
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.effective_date || !form.gross_salary) {
      toast.warning("Date et salaire brut obligatoires");
      return;
    }
    setSaving(true);
    const payload = {
      effective_date: form.effective_date,
      gross_salary: parseFloat(form.gross_salary),
      bonus: form.bonus ? parseFloat(form.bonus) : null,
      salary_type: form.salary_type,
      note: form.note,
    };
    try {
      if (editingEntry) {
        await axios.put(`${API}/salary-history/${editingEntry.id}`, payload);
        toast.success("Entrée modifiée");
      } else {
        await axios.post(`${API}/users/${userId}/salary-history`, payload);
        toast.success("Entrée ajoutée");
      }
      setShowForm(false);
      setEditingEntry(null);
      setForm({ effective_date: "", gross_salary: "", bonus: "", salary_type: "mensuel", note: "" });
      fetchEntries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API}/salary-history/${id}`);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Entrée supprimée");
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(null);
    }
  };

  // Chart data — sorted chronologically
  const chartData = [...entries]
    .sort((a, b) => a.effective_date.localeCompare(b.effective_date))
    .map((e) => ({
      label: e.effective_date.substring(0, 7),
      brut: e.gross_salary || null,
      prime: e.bonus || null,
    }));

  // Yearly cumul — entries are newest-first, so first hit per year = latest salary
  const yearlyStats = Object.values(
    [...entries].reduce((acc, e) => {
      const year = e.effective_date?.split("-")[0];
      if (!acc[year]) acc[year] = { year, lastGross: null, totalBonus: 0 };
      if (acc[year].lastGross === null && e.gross_salary) acc[year].lastGross = e.gross_salary;
      acc[year].totalBonus += e.bonus || 0;
      return acc;
    }, {})
  ).sort((a, b) => b.year - a.year);

  const fmtEuro = (val) =>
    val != null
      ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(val)
      : "—";

  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white dark:bg-[#112240] border border-gray-200 dark:border-[#1A3050] rounded-lg p-3 shadow-lg text-xs space-y-1">
        <p className="font-semibold text-[#111827] dark:text-[#D6E4F5]">{label}</p>
        {payload.map((p) => (
          <p key={p.dataKey} style={{ color: p.color }}>{p.name} : {fmtEuro(p.value)}</p>
        ))}
      </div>
    );
  };

  if (loading) return <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin text-[#1A4D2E]" /></div>;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-[#4B5563] dark:text-[#7EA8CE]">
          {entries.length} entrée(s) dans l'historique
        </p>
        <Button size="sm" onClick={openAdd}
          className="bg-[#1A4D2E] hover:bg-[#143D24] dark:bg-[#166534] dark:hover:bg-[#14532d] text-white gap-1">
          <Plus className="w-4 h-4" /> Ajouter
        </Button>
      </div>

      {/* Add / Edit form */}
      {showForm && (
        <div className="border dark:border-[#1A3050] rounded-xl p-4 space-y-3 bg-gray-50 dark:bg-[#112240]/60">
          <p className="text-xs font-semibold text-[#4B5563] dark:text-[#7EA8CE] uppercase tracking-wide">
            {editingEntry ? "Modifier l'entrée" : "Nouvelle entrée"}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs dark:text-[#D6E4F5]">Date effective *</Label>
              <Input type="date" value={form.effective_date}
                onChange={(e) => setForm((p) => ({ ...p, effective_date: e.target.value }))}
                className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs dark:text-[#D6E4F5]">Type</Label>
              <Select value={form.salary_type} onValueChange={(v) => setForm((p) => ({ ...p, salary_type: v }))}>
                <SelectTrigger className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mensuel">Mensuel</SelectItem>
                  <SelectItem value="annuel">Annuel</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs dark:text-[#D6E4F5]">Brut (€) *</Label>
              <Input type="number" value={form.gross_salary} placeholder="0"
                onChange={(e) => setForm((p) => ({ ...p, gross_salary: e.target.value }))}
                className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs dark:text-[#D6E4F5]">Prime (€)</Label>
              <Input type="number" value={form.bonus} placeholder="0"
                onChange={(e) => setForm((p) => ({ ...p, bonus: e.target.value }))}
                className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs dark:text-[#D6E4F5]">Note</Label>
            <Input value={form.note} placeholder="Augmentation, promotion..."
              onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
              className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving}
              className="bg-[#1A4D2E] hover:bg-[#143D24] dark:bg-[#166534] dark:hover:bg-[#14532d] text-white">
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
              {editingEntry ? "Mettre à jour" : "Enregistrer"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setShowForm(false); setEditingEntry(null); }}
              className="dark:border-[#1A3050] dark:text-[#D6E4F5] dark:hover:bg-[#112240]">
              Annuler
            </Button>
          </div>
        </div>
      )}

      {entries.length === 0 ? (
        <div className="text-center py-10 text-[#9CA3AF] dark:text-[#3D607E]">
          <Euro className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Aucun historique de rémunération</p>
        </div>
      ) : (
        <>
          {/* Chart */}
          {chartData.length >= 2 && (
            <div className="border dark:border-[#1A3050] rounded-xl p-4 bg-white dark:bg-[#112240]/30">
              <h4 className="text-sm font-semibold text-[#111827] dark:text-[#D6E4F5] mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#1A4D2E] dark:text-[#4CAF70]" />
                Évolution de la rémunération
              </h4>
              <ResponsiveContainer width="100%" height={200}>
                <ComposedChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#6B7280" strokeOpacity={0.2} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#9CA3AF" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#9CA3AF" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} width={36} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: "11px" }} />
                  <Line type="monotone" dataKey="brut" name="Brut" stroke="#1A4D2E" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                  <Bar dataKey="prime" name="Prime" fill="#F59E0B" opacity={0.85} radius={[3, 3, 0, 0]} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Yearly cumul */}
          {yearlyStats.length > 0 && (
            <div className="border dark:border-[#1A3050] rounded-xl p-4 bg-white dark:bg-[#112240]/30">
              <h4 className="text-sm font-semibold text-[#111827] dark:text-[#D6E4F5] mb-3 flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-[#1A4D2E] dark:text-[#4CAF70]" />
                Cumul par année
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {yearlyStats.map(({ year, lastGross, totalBonus }) => (
                  <div key={year} className="rounded-lg border dark:border-[#1A3050] bg-gray-50 dark:bg-[#112240] p-3 flex justify-between items-center gap-2">
                    <span className="font-semibold text-[#111827] dark:text-[#D6E4F5] text-sm w-12">{year}</span>
                    <div className="flex gap-4 text-xs text-right">
                      <div>
                        <p className="text-[#9CA3AF] dark:text-[#3D607E]">Brut</p>
                        <p className="font-medium text-[#111827] dark:text-[#D6E4F5]">{fmtEuro(lastGross)}</p>
                      </div>
                      {totalBonus > 0 && (
                        <div>
                          <p className="text-[#9CA3AF] dark:text-[#3D607E]">Primes</p>
                          <p className="font-medium text-amber-600 dark:text-amber-400">{fmtEuro(totalBonus)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* History — mobile cards */}
          <div className="sm:hidden border dark:border-[#1A3050] rounded-xl divide-y divide-gray-100 dark:divide-[#1A3050] overflow-hidden">
            {entries.map((e) => (
              <div key={e.id} className="p-4 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-medium text-sm dark:text-[#D6E4F5]">{e.effective_date}</span>
                    <Badge variant="outline" className="text-xs dark:border-[#1A3050] dark:text-[#7EA8CE]">{e.salary_type}</Badge>
                  </div>
                  <div className="flex gap-3 text-xs text-[#4B5563] dark:text-[#7EA8CE] mt-1">
                    <span>Brut : {fmt(e.gross_salary)}</span>
                    {e.bonus != null && <span className="text-amber-600 dark:text-amber-400">Prime : {fmt(e.bonus)}</span>}
                  </div>
                  {e.note && <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E] mt-1 truncate">{e.note}</p>}
                </div>
                <div className="flex gap-0.5 shrink-0">
                  <Button variant="ghost" size="icon" className="w-7 h-7 text-[#4B5563] dark:text-[#7EA8CE] hover:text-[#1A4D2E] hover:bg-[#E8F5E9] dark:hover:bg-[#1A4D2E]/20"
                    onClick={() => openEdit(e)} title="Modifier">
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="icon" className="w-7 h-7 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    onClick={() => handleDelete(e.id)} disabled={deleting === e.id} title="Supprimer">
                    {deleting === e.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* History table — desktop */}
          <div className="hidden sm:block overflow-x-auto border dark:border-[#1A3050] rounded-xl">
            <Table>
              <TableHeader>
                <TableRow className="dark:border-[#1A3050]">
                  <TableHead className="dark:text-[#7EA8CE]">Date</TableHead>
                  <TableHead className="dark:text-[#7EA8CE]">Type</TableHead>
                  <TableHead className="dark:text-[#7EA8CE]">Brut</TableHead>
                  <TableHead className="dark:text-[#7EA8CE]">Prime</TableHead>
                  <TableHead className="dark:text-[#7EA8CE]">Note</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((e) => (
                  <TableRow key={e.id} className="dark:border-[#1A3050] dark:hover:bg-[#112240]/50">
                    <TableCell className="font-medium dark:text-[#D6E4F5]">{e.effective_date}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs dark:border-[#1A3050] dark:text-[#7EA8CE]">
                        {e.salary_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="dark:text-[#D6E4F5]">{fmt(e.gross_salary)}</TableCell>
                    <TableCell className="dark:text-[#D6E4F5]">{fmt(e.bonus)}</TableCell>
                    <TableCell className="text-sm text-[#9CA3AF] dark:text-[#3D607E] max-w-[140px] truncate">{e.note || "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-0.5">
                        <Button variant="ghost" size="icon" className="w-7 h-7 text-[#4B5563] dark:text-[#7EA8CE] hover:text-[#1A4D2E] hover:bg-[#E8F5E9] dark:hover:bg-[#1A4D2E]/20"
                          onClick={() => openEdit(e)} title="Modifier">
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="w-7 h-7 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          onClick={() => handleDelete(e.id)} disabled={deleting === e.id} title="Supprimer">
                          {deleting === e.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
};

// ── Tab: Suivi RH ─────────────────────────────────────────────────────────────

const TabSuiviRH = ({ userId, currentUser }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    date: today, note_type: "entretien_annuel", content: "", is_private: false,
  });

  const fetchNotes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users/${userId}/rh-notes`);
      setNotes(res.data);
    } catch (e) {
      toast.error("Erreur chargement notes");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const openAdd = () => {
    setEditingNote(null);
    setForm({ date: today, note_type: "entretien_annuel", content: "", is_private: false });
    setShowForm(true);
  };

  const openEdit = (n) => {
    setEditingNote(n);
    setForm({ date: n.date, note_type: n.note_type, content: n.content, is_private: n.is_private });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.content.trim()) { toast.warning("Contenu obligatoire"); return; }
    setSaving(true);
    try {
      if (editingNote) {
        await axios.put(`${API}/rh-notes/${editingNote.id}`, form);
        toast.success("Note modifiée");
      } else {
        await axios.post(`${API}/users/${userId}/rh-notes`, form);
        toast.success("Note ajoutée");
      }
      setShowForm(false);
      setEditingNote(null);
      setForm({ date: today, note_type: "entretien_annuel", content: "", is_private: false });
      fetchNotes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API}/rh-notes/${id}`);
      setNotes((p) => p.filter((n) => n.id !== id));
      toast.success("Note supprimée");
    } finally {
      setDeleting(null);
    }
  };

  if (loading) return <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin text-[#1A4D2E]" /></div>;

  const canEdit = ROLES_FULL_ACCESS.includes(currentUser?.role);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[#4B5563] dark:text-[#7EA8CE]">{notes.length} note(s)</p>
        {canEdit && (
          <Button size="sm" onClick={openAdd}
            className="bg-[#1A4D2E] hover:bg-[#143D24] dark:bg-[#166534] dark:hover:bg-[#14532d] text-white gap-1">
            <Plus className="w-4 h-4" /> Ajouter
          </Button>
        )}
      </div>

      {canEdit && showForm && (
        <div className="border dark:border-[#1A3050] rounded-xl p-4 space-y-3 bg-gray-50 dark:bg-[#112240]/60">
          <p className="text-xs font-semibold text-[#4B5563] dark:text-[#7EA8CE] uppercase tracking-wide">
            {editingNote ? "Modifier la note" : "Nouvelle note"}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs dark:text-[#D6E4F5]">Date</Label>
              <Input type="date" value={form.date}
                onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs dark:text-[#D6E4F5]">Type</Label>
              <Select value={form.note_type} onValueChange={(v) => setForm((p) => ({ ...p, note_type: v }))}>
                <SelectTrigger className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {NOTE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs dark:text-[#D6E4F5]">Contenu *</Label>
            <Textarea value={form.content} rows={3} placeholder="Détails de la note..."
              onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
              className="dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer dark:text-[#D6E4F5]">
            <input type="checkbox" checked={form.is_private}
              onChange={(e) => setForm((p) => ({ ...p, is_private: e.target.checked }))}
              className="rounded" />
            <Lock className="w-3 h-3" /> Note confidentielle (RH/Admin uniquement)
          </label>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving}
              className="bg-[#1A4D2E] hover:bg-[#143D24] dark:bg-[#166534] dark:hover:bg-[#14532d] text-white">
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
              {editingNote ? "Mettre à jour" : "Enregistrer"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setShowForm(false); setEditingNote(null); }}
              className="dark:border-[#1A3050] dark:text-[#D6E4F5] dark:hover:bg-[#112240]">Annuler</Button>
          </div>
        </div>
      )}

      {notes.length === 0 ? (
        <div className="text-center py-10 text-[#9CA3AF] dark:text-[#3D607E]">
          <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Aucune note de suivi RH</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((n) => (
            <div key={n.id} className={`rounded-xl p-4 border ${n.is_private ? "border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/10" : "border-gray-100 dark:border-[#1A3050] bg-white dark:bg-[#112240]/40"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`text-xs ${NOTE_COLORS[n.note_type] || NOTE_COLORS.autre}`}>
                    {NOTE_TYPES.find((t) => t.value === n.note_type)?.label || n.note_type}
                  </Badge>
                  {n.is_private && (
                    <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                      <Lock className="w-3 h-3" /> Confidentiel
                    </span>
                  )}
                  <span className="text-xs text-[#9CA3AF] dark:text-[#3D607E]">{n.date}</span>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="w-6 h-6 text-[#4B5563] dark:text-[#7EA8CE] hover:text-[#1A4D2E] hover:bg-[#E8F5E9] dark:hover:bg-[#1A4D2E]/20"
                      onClick={() => openEdit(n)} title="Modifier">
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="w-6 h-6 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                      onClick={() => handleDelete(n.id)} disabled={deleting === n.id} title="Supprimer">
                      {deleting === n.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    </Button>
                  </div>
                )}
              </div>
              <p className="mt-2 text-sm text-[#111827] dark:text-[#D6E4F5] whitespace-pre-wrap">{n.content}</p>
              <p className="mt-1 text-xs text-[#9CA3AF] dark:text-[#3D607E]">par {n.author_name}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Tab: Congés & Absences ────────────────────────────────────────────────────

const TabCongesAbsences = ({ userId }) => {
  const [leaves, setLeaves] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [lRes, aRes] = await Promise.all([
          axios.get(`${API}/users/${userId}/leaves`),
          axios.get(`${API}/users/${userId}/absences`),
        ]);
        setLeaves(lRes.data.slice(0, 20));
        setAbsences(aRes.data.slice(0, 20));
      } catch {
        toast.error("Erreur chargement congés/absences");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  if (loading) return <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin text-[#1A4D2E]" /></div>;

  return (
    <div className="space-y-5">
      {/* Congés */}
      <div className="border dark:border-[#1A3050] rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-[#112240]/60 border-b dark:border-[#1A3050] flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#1A4D2E] dark:text-[#4CAF70]" />
          <h3 className="font-semibold text-sm text-[#111827] dark:text-[#D6E4F5]">Congés</h3>
          <span className="ml-auto text-xs text-[#9CA3AF] dark:text-[#3D607E]">{leaves.length} demande(s)</span>
        </div>
        {leaves.length === 0 ? (
          <div className="text-center py-8 text-[#9CA3AF] dark:text-[#3D607E]">
            <Calendar className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Aucun congé enregistré</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-[#1A3050]">
            {leaves.map((l) => {
              const st = STATUS_LEAVE[l.status] || STATUS_LEAVE.pending;
              const Icon = st.icon;
              return (
                <div key={l.id} className="p-3 bg-white dark:bg-[#112240]/20">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <p className="font-medium text-sm dark:text-[#D6E4F5] truncate">{l.leave_type}</p>
                      <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E] mt-0.5">{l.start_date} → {l.end_date}</p>
                    </div>
                    <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium shrink-0 ${st.badgeClass}`}>
                      <Icon className="w-3 h-3" /> {st.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Absences */}
      <div className="border dark:border-[#1A3050] rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-[#112240]/60 border-b dark:border-[#1A3050] flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#1A4D2E] dark:text-[#4CAF70]" />
          <h3 className="font-semibold text-sm text-[#111827] dark:text-[#D6E4F5]">Absences</h3>
          <span className="ml-auto text-xs text-[#9CA3AF] dark:text-[#3D607E]">{absences.length} absence(s)</span>
        </div>
        {absences.length === 0 ? (
          <div className="text-center py-8 text-[#9CA3AF] dark:text-[#3D607E]">
            <Clock className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Aucune absence enregistrée</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-[#1A3050]">
            {absences.map((a) => (
              <div key={a.id} className="p-3 bg-white dark:bg-[#112240]/20">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-medium text-sm dark:text-[#D6E4F5] truncate">{a.absence_type}</p>
                    <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E] mt-0.5">
                      {a.date}{a.reason ? ` — ${a.reason}` : ""}
                    </p>
                  </div>
                  <span className="text-xs font-medium bg-gray-100 dark:bg-[#112240] text-[#4B5563] dark:text-[#7EA8CE] px-2 py-1 rounded-full shrink-0">
                    {a.duration_hours}h
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Tab: Équipe ───────────────────────────────────────────────────────────────

const TabEquipe = ({ userData, allUsers, canEdit, onRefresh, onOpenUser }) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addingId, setAddingId] = useState("");

  const fetchTeam = useCallback(async () => {
    setLoadingTeam(true);
    try {
      const res = await axios.get(`${API}/users/team/${userData.id}`);
      setTeamMembers(res.data);
    } catch {
      setTeamMembers([]);
    } finally {
      setLoadingTeam(false);
    }
  }, [userData.id]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const manager = allUsers.find((u) => u.id === userData.manager_id) || null;
  const siblings = userData.manager_id
    ? allUsers.filter((u) => u.manager_id === userData.manager_id && u.id !== userData.id)
    : [];
  const available = allUsers.filter(
    (u) => u.id !== userData.id && !teamMembers.find((t) => t.id === u.id) && u.role !== "admin"
  );

  const saveTeam = async (newTeam) => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/${userData.id}/team`, { user_ids: newTeam.map((u) => u.id) });
      toast.success("Équipe mise à jour");
      fetchTeam();
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => {
    if (!addingId) return;
    const member = allUsers.find((u) => u.id === addingId);
    if (member) saveTeam([...teamMembers, member]);
    setAddingId("");
  };

  const handleRemove = (uid) => saveTeam(teamMembers.filter((u) => u.id !== uid));

  // Ligne individuelle de l'organigramme
  const PersonRow = ({ person, isSelf = false, onRemove = null }) => {
    const rc = ROLE_CONFIG[person.role] || ROLE_CONFIG.collaborateur;
    return (
      <div className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
        isSelf
          ? "bg-[#1A4D2E]/10 dark:bg-[#1A4D2E]/20 ring-1 ring-[#1A4D2E]/30"
          : "hover:bg-gray-50 dark:hover:bg-[#112240]/50"
      }`}>
        <Avatar className="w-9 h-9 shrink-0">
          <AvatarImage src={person.avatar_url} />
          <AvatarFallback className="text-xs bg-[#E8F5E9] dark:bg-[#1A3A6A] text-[#1A4D2E] dark:text-[#4B8BF4] font-semibold">
            {getInitials(person.first_name, person.last_name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium dark:text-[#D6E4F5] truncate">
            {person.first_name} {person.last_name}
            {isSelf && <span className="ml-1 text-xs text-[#1A4D2E] dark:text-[#4CAF70] font-normal">(fiche affichée)</span>}
          </p>
          <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E] truncate">
            {person.position || person.department || person.email}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Badge className={`text-xs hidden sm:inline-flex ${rc.className}`}>{rc.label}</Badge>
          {!isSelf && onOpenUser && (
            <Button variant="ghost" size="icon" className="w-7 h-7 text-[#1A4D2E] dark:text-[#4B8BF4] hover:bg-[#E8F5E9] dark:hover:bg-[#112240]"
              onClick={() => onOpenUser(person.id)} title="Voir la fiche">
              <ExternalLink className="w-3.5 h-3.5" />
            </Button>
          )}
          {onRemove && canEdit && (
            <Button variant="ghost" size="icon" className="w-7 h-7 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              onClick={() => onRemove(person.id)}>
              <X className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3">

      {/* Manager */}
      {manager && (
        <div className="border dark:border-[#1A3050] rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 bg-gray-50 dark:bg-[#112240]/60 border-b dark:border-[#1A3050]">
            <p className="text-xs font-semibold text-[#9CA3AF] dark:text-[#3D607E] uppercase tracking-wide">Manager</p>
          </div>
          <div className="p-2">
            <PersonRow person={manager} />
          </div>
        </div>
      )}

      {/* Cette personne + collègues */}
      <div className="border dark:border-[#1A3050] rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 bg-gray-50 dark:bg-[#112240]/60 border-b dark:border-[#1A3050] flex items-center justify-between">
          <p className="text-xs font-semibold text-[#9CA3AF] dark:text-[#3D607E] uppercase tracking-wide">
            {manager ? `Équipe de ${manager.first_name} ${manager.last_name}` : "Équipe"}
          </p>
          {saving && <Loader2 className="w-3 h-3 animate-spin text-[#1A4D2E]" />}
        </div>
        <div className="p-2 space-y-0.5">
          <PersonRow person={userData} isSelf />
          {siblings.map((s) => <PersonRow key={s.id} person={s} />)}
        </div>
      </div>

      {/* Équipe directe */}
      {loadingTeam ? (
        <div className="flex justify-center py-3">
          <Loader2 className="w-4 h-4 animate-spin text-[#1A4D2E]" />
        </div>
      ) : teamMembers.length > 0 && (
        <div className="border dark:border-[#1A3050] rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 bg-gray-50 dark:bg-[#112240]/60 border-b dark:border-[#1A3050]">
            <p className="text-xs font-semibold text-[#9CA3AF] dark:text-[#3D607E] uppercase tracking-wide">
              Équipe directe · {teamMembers.length} membre{teamMembers.length > 1 ? "s" : ""}
            </p>
          </div>
          <div className="p-2 space-y-0.5">
            {teamMembers.map((u) => (
              <PersonRow key={u.id} person={u} onRemove={handleRemove} />
            ))}
          </div>
        </div>
      )}

      {!loadingTeam && !manager && teamMembers.length === 0 && siblings.length === 0 && (
        <div className="border dark:border-[#1A3050] rounded-xl p-6 text-center text-[#9CA3AF] dark:text-[#3D607E]">
          <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Aucun rattachement défini</p>
        </div>
      )}

      {/* Ajouter un membre (HR/admin uniquement) */}
      {canEdit && (
        <div className="border dark:border-[#1A3050] rounded-xl p-4 bg-gray-50 dark:bg-[#112240]/60 space-y-2">
          <p className="text-xs font-semibold text-[#4B5563] dark:text-[#7EA8CE] uppercase tracking-wide">Ajouter un membre à l'équipe</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Select value={addingId} onValueChange={setAddingId}>
              <SelectTrigger className="flex-1 dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]">
                <SelectValue placeholder="Sélectionner un collaborateur..." />
              </SelectTrigger>
              <SelectContent>
                {available.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} — {u.department || "—"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleAdd} disabled={!addingId || saving}
              className="bg-[#1A4D2E] hover:bg-[#143D24] text-white gap-1 sm:w-auto w-full">
              <Plus className="w-4 h-4" /> Ajouter
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Tab: Onboarding ───────────────────────────────────────────────────────────

const STEP_TYPE_ICONS = {
  team_presentation: "👥",
  document_reading:  "📄",
  subject_takeover:  "🎯",
  meeting_2w:        "📅",
  meeting_1m:        "📅",
  meeting_3m:        "📅",
};

const TabOnboarding = ({ userId, canManage }) => {
  const [onboarding, setOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [starting, setStarting] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState({});

  const fetchOnboarding = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/onboarding/${userId}`);
      setOnboarding(r.data);
      setNotFound(false);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error("Erreur chargement onboarding");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchOnboarding(); }, [fetchOnboarding]);

  const handleStart = async () => {
    setStarting(true);
    try {
      await axios.post(`${API}/onboarding/start/${userId}`);
      toast.success("Onboarding démarré");
      fetchOnboarding();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setStarting(false);
    }
  };

  const handleComplete = async (stepId, notes = undefined) => {
    try {
      await axios.put(`${API}/onboarding/steps/${stepId}/complete`, { notes });
      toast.success("Étape complétée");
      fetchOnboarding();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const handleSkip = async (stepId) => {
    try {
      await axios.put(`${API}/onboarding/steps/${stepId}/skip`);
      toast.success("Étape ignorée");
      fetchOnboarding();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#1A4D2E]" /></div>;
  }

  if (notFound) {
    return (
      <div className="text-center py-10 border dark:border-[#1A3050] rounded-xl">
        <ClipboardList className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-[#3D607E]" />
        <p className="text-sm text-gray-500 dark:text-[#7EA8CE] mb-4">Aucun onboarding démarré</p>
        {canManage && (
          <Button onClick={handleStart} disabled={starting}
            className="bg-[#1A4D2E] hover:bg-[#143D24] text-white gap-1">
            {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Démarrer l'onboarding
          </Button>
        )}
      </div>
    );
  }

  if (!onboarding) return null;

  const completedCount = onboarding.steps.filter(s => s.status === "completed").length;
  const totalCount = onboarding.steps.length;
  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className="space-y-4">
      {/* Status + Progress */}
      <div className="border dark:border-[#1A3050] rounded-xl p-4 bg-gray-50 dark:bg-[#112240]/60">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-[#1A4D2E] dark:text-[#4CAF70]" />
            <span className="text-sm font-medium dark:text-[#D6E4F5]">
              {onboarding.status === "completed" ? "Onboarding terminé" : "Onboarding en cours"}
            </span>
          </div>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            onboarding.status === "completed"
              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
              : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
          }`}>
            {onboarding.status === "completed" ? "Terminé" : "En cours"}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-gray-200 dark:bg-[#1A3050] rounded-full h-2">
            <div className="bg-[#1A4D2E] dark:bg-[#4CAF70] h-2 rounded-full transition-all"
              style={{ width: `${progressPct}%` }} />
          </div>
          <span className="text-xs text-gray-500 dark:text-[#7EA8CE] shrink-0">
            {completedCount}/{totalCount} étapes
          </span>
        </div>
        <p className="text-xs text-gray-400 dark:text-[#3D607E] mt-2">
          Démarré le {dateFnsFormat(new Date(onboarding.started_at), "dd MMMM yyyy", { locale: dateFnsFr })}
          {onboarding.completed_at && ` · Terminé le ${dateFnsFormat(new Date(onboarding.completed_at), "dd MMMM yyyy", { locale: dateFnsFr })}`}
        </p>
      </div>

      {/* Étapes */}
      <div className="space-y-2">
        {onboarding.steps.map((step) => {
          const isComplete = step.status === "completed";
          const isSkipped = step.status === "skipped";
          const isPending = step.status === "pending";
          const isExpanded = expandedNotes[step.id];

          return (
            <div key={step.id} className={`border dark:border-[#1A3050] rounded-xl p-3 transition-colors ${
              isComplete ? "bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800/30" :
              isSkipped ? "bg-gray-50 dark:bg-[#0B1628] opacity-70" :
              "bg-white dark:bg-[#0B1628]"
            }`}>
              <div className="flex items-start gap-3">
                <div className="text-lg shrink-0 mt-0.5">{STEP_TYPE_ICONS[step.step_type] || "📋"}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-sm font-medium ${isComplete ? "line-through text-gray-400 dark:text-gray-500" : "dark:text-[#D6E4F5]"}`}>
                      {step.title}
                    </p>
                    <div className="flex items-center gap-1 shrink-0">
                      {isComplete && <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />}
                      {isSkipped && <SkipForward className="w-4 h-4 text-gray-400" />}
                      {isPending && <Clock className="w-4 h-4 text-amber-500" />}
                    </div>
                  </div>
                  {step.due_date && (
                    <p className="text-xs text-gray-400 dark:text-[#3D607E] mt-0.5">
                      Échéance : {step.due_date}
                    </p>
                  )}
                  {step.notes && (
                    <p className="text-xs text-gray-500 dark:text-[#7EA8CE] mt-1 italic">{step.notes}</p>
                  )}
                  {step.completed_at && (
                    <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                      Complétée le {dateFnsFormat(new Date(step.completed_at), "dd/MM/yyyy", { locale: dateFnsFr })}
                    </p>
                  )}
                </div>
              </div>

              {canManage && isPending && (
                <div className="mt-2 pt-2 border-t dark:border-[#1A3050] flex gap-2">
                  <Button size="sm" onClick={() => handleComplete(step.id)}
                    className="bg-[#1A4D2E] hover:bg-[#143D24] text-white h-7 text-xs gap-1">
                    <CheckCircle className="w-3 h-3" /> Compléter
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleSkip(step.id)}
                    className="dark:border-[#1A3050] dark:text-[#7EA8CE] dark:hover:bg-[#112240] h-7 text-xs gap-1">
                    <SkipForward className="w-3 h-3" /> Passer
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Main Sheet ────────────────────────────────────────────────────────────────

const UserFicheSheet = ({ userId, open, onClose, allUsers, onRefresh, onOpenUser }) => {
  const { user: currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const canEditInfo = ROLES_FULL_ACCESS.includes(currentUser?.role);
  const canSeeRemuneration = ROLES_FULL_ACCESS.includes(currentUser?.role);

  const fetchUser = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users/${userId}`);
      setUserData(res.data);
    } catch {
      toast.error("Erreur chargement utilisateur");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [userId, onClose]);

  useEffect(() => {
    if (open && userId) fetchUser();
  }, [open, userId, fetchUser]);

  const handleSaved = () => {
    fetchUser();
    onRefresh();
  };

  if (!open) return null;

  const rc = userData ? (ROLE_CONFIG[userData.role] || ROLE_CONFIG.collaborateur) : null;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto dark:bg-[#0B1628] dark:border-[#1A3050] p-0"
      >
        {loading || !userData ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="p-6 border-b dark:border-[#1A3050] bg-gradient-to-r from-[#1A4D2E]/5 to-transparent dark:from-[#1A4D2E]/20">
              <SheetHeader>
                <div className="flex items-center gap-4">
                  <Avatar className="w-16 h-16 border-2 border-[#1A4D2E]/20">
                    <AvatarImage src={userData.avatar_url} />
                    <AvatarFallback className="bg-[#E8F5E9] dark:bg-emerald-900/40 text-[#1A4D2E] dark:text-emerald-400 text-xl font-bold">
                      {getInitials(userData.first_name, userData.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <SheetTitle className="text-xl font-bold dark:text-white truncate">
                      {userData.first_name} {userData.last_name}
                    </SheetTitle>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{userData.email}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{userData.position}{userData.position && userData.department ? " · " : ""}{userData.department}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={`text-xs ${rc.className}`}>{rc.label}</Badge>
                      <Badge className={userData.is_active
                        ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs"
                        : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 text-xs"}>
                        {userData.is_active ? "Actif" : "Inactif"}
                      </Badge>
                      {userData.hire_date && (
                        <span className="text-xs text-gray-400">Depuis le {userData.hire_date}</span>
                      )}
                    </div>
                  </div>
                </div>
              </SheetHeader>
            </div>

            {/* Tabs */}
            <div className="p-6">
              <Tabs defaultValue="infos">
                <TabsList className="mb-6 flex-wrap h-auto gap-1 dark:bg-[#112240]">
                  <TabsTrigger value="infos" className="gap-1 dark:data-[state=active]:bg-[#1A4D2E] dark:text-[#7EA8CE] dark:data-[state=active]:text-white">
                    <User className="w-3 h-3" /> Informations
                  </TabsTrigger>
                  {canSeeRemuneration && (
                    <TabsTrigger value="remuneration" className="gap-1 dark:data-[state=active]:bg-[#1A4D2E] dark:text-[#7EA8CE] dark:data-[state=active]:text-white">
                      <Euro className="w-3 h-3" /> Rémunération
                    </TabsTrigger>
                  )}
                  {ROLES_MANAGEMENT.includes(currentUser?.role) && (
                    <TabsTrigger value="suivi" className="gap-1 dark:data-[state=active]:bg-[#1A4D2E] dark:text-[#7EA8CE] dark:data-[state=active]:text-white">
                      <FileText className="w-3 h-3" /> Suivi RH
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="conges" className="gap-1 dark:data-[state=active]:bg-[#1A4D2E] dark:text-[#7EA8CE] dark:data-[state=active]:text-white">
                    <Calendar className="w-3 h-3" /> Congés & Absences
                  </TabsTrigger>
                  <TabsTrigger value="equipe" className="gap-1 dark:data-[state=active]:bg-[#1A4D2E] dark:text-[#7EA8CE] dark:data-[state=active]:text-white">
                    <Users className="w-3 h-3" /> Équipe
                  </TabsTrigger>
                  {ROLES_MANAGEMENT.includes(currentUser?.role) && (
                    <TabsTrigger value="onboarding" className="gap-1 dark:data-[state=active]:bg-[#1A4D2E] dark:text-[#7EA8CE] dark:data-[state=active]:text-white">
                      <ClipboardList className="w-3 h-3" /> Onboarding
                    </TabsTrigger>
                  )}
                </TabsList>

                <TabsContent value="infos">
                  <TabInfos
                    userData={userData}
                    allUsers={allUsers}
                    canEdit={canEditInfo}
                    onSaved={handleSaved}
                  />
                </TabsContent>

                {canSeeRemuneration && (
                  <TabsContent value="remuneration">
                    <TabRemuneration userId={userData.id} />
                  </TabsContent>
                )}

                {ROLES_MANAGEMENT.includes(currentUser?.role) && (
                  <TabsContent value="suivi">
                    <TabSuiviRH userId={userData.id} currentUser={currentUser} />
                  </TabsContent>
                )}

                <TabsContent value="conges">
                  <TabCongesAbsences userId={userData.id} />
                </TabsContent>

                <TabsContent value="equipe">
                  <TabEquipe
                    userData={userData}
                    allUsers={allUsers}
                    canEdit={canEditInfo}
                    onRefresh={() => { fetchUser(); onRefresh(); }}
                    onOpenUser={onOpenUser}
                  />
                </TabsContent>

                {ROLES_MANAGEMENT.includes(currentUser?.role) && (
                  <TabsContent value="onboarding">
                    <TabOnboarding
                      userId={userData.id}
                      canManage={ROLES_MANAGEMENT.includes(currentUser?.role)}
                    />
                  </TabsContent>
                )}
              </Tabs>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default UserFicheSheet;
